import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, CheckCircle, Timer, Activity } from 'lucide-react';
import { Button } from './ui/button';
import PoseCameraAdvanced from './PoseCameraAdvanced';
import PoseCameraErrorBoundary from './PoseCameraErrorBoundary';

// Parse a prescription string into seconds for a timed hold.
// "30 sec" → 30, "1 min" → 60, "45s" → 45, "1.5 min" → 90. Falls back to 40.
const parseSeconds = (raw) => {
  const s = String(raw || '').toLowerCase();
  const minMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:min|minute)/);
  if (minMatch) return Math.round(parseFloat(minMatch[1]) * 60);
  const secMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:s|sec|second)/);
  if (secMatch) return Math.round(parseFloat(secMatch[1]));
  const bare = s.match(/^\s*(\d+)\s*$/);
  if (bare) return parseInt(bare[1], 10);
  return 40;
};

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Guided panel for movements the camera CANNOT reliably rep-count.
 *  - mode 'timed'  → a countdown timer (wall sit, plank, yoga hold).
 *  - mode 'follow' → target + coaching cue ("do these, then Done").
 * Both show a skeleton "mirror" camera so the user can still see themselves —
 * but with NO rep counter and NO form scoring (we never fake a count).
 */
const GuidedExercisePanel = ({
  mode = 'follow',
  exercise = {},
  exerciseName = '',
  exerciseDefinition = null,
  immersive = false,
  onCameraStateChange,
}) => {
  const target = exercise.reps || exercise.reps_or_seconds || '';
  const totalSeconds = parseSeconds(target);
  const cue = (exerciseDefinition?.how_to || '').split(/[.\n]/)[0]?.trim() || '';

  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const tickRef = useRef(null);

  // Reset the timer whenever the exercise changes.
  const resetKey = `${exerciseName}`;
  const [prevKey, setPrevKey] = useState(resetKey);
  if (resetKey !== prevKey) {
    setPrevKey(resetKey);
    setSecondsLeft(totalSeconds);
    setRunning(false);
    setDone(false);
  }

  useEffect(() => {
    if (!running) return undefined;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { setRunning(false); setDone(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  const toggle = useCallback(() => {
    if (done) { setSecondsLeft(totalSeconds); setDone(false); setRunning(true); return; }
    setRunning((r) => !r);
  }, [done, totalSeconds]);

  const reset = useCallback(() => {
    setRunning(false); setDone(false); setSecondsLeft(totalSeconds);
  }, [totalSeconds]);

  const pct = totalSeconds ? ((totalSeconds - secondsLeft) / totalSeconds) * 100 : 0;

  return (
    <div className={immersive ? 'flex-1 min-h-0 flex flex-col' : ''} data-testid="guided-exercise-panel">
      {/* Guidance banner — clearly explains why there's no rep counter */}
      <div
        className="mb-3 bg-cyan-500/10 border border-cyan-500/30 rounded-sm p-3 flex items-start gap-3"
        data-testid={`guided-banner-${mode}`}
      >
        <div className="w-9 h-9 rounded-sm bg-cyan-500/20 text-cyan-300 flex items-center justify-center flex-shrink-0">
          {mode === 'timed' ? <Timer className="w-5 h-5" /> : <Activity className="w-5 h-5" />}
        </div>
        <div className="min-w-0">
          <p className="font-secondary font-bold text-sm text-white">
            {mode === 'timed' ? 'Hold this one' : 'Follow along'}
            <span className="text-cyan-300"> · {target || (mode === 'timed' ? `${fmt(totalSeconds)}` : 'do the movement')}</span>
          </p>
          <p className="text-xs text-zinc-400 leading-snug mt-0.5">
            {mode === 'timed'
              ? 'This is a hold, so I track it on a timer instead of counting reps.'
              : "This is a mobility / warm-up move — I can't count it like a lift, so just follow along and tap Done."}
            {cue ? ` ${cue}.` : ''}
          </p>
        </div>
      </div>

      {/* Timed: countdown */}
      {mode === 'timed' && (
        <div className="mb-3 bg-zinc-900/60 border border-zinc-800 rounded-sm p-4" data-testid="hold-timer">
          <div className="flex items-center justify-center gap-5">
            <button
              onClick={toggle}
              className="w-14 h-14 rounded-full bg-cyan-400 text-black flex items-center justify-center hover:bg-cyan-300 transition-colors"
              data-testid="hold-timer-toggle"
              aria-label={running ? 'Pause' : 'Start'}
            >
              {done ? <RotateCcw className="w-6 h-6" /> : running ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
            </button>
            <div className="text-center">
              <p className="hpme-number text-5xl text-white leading-none" data-testid="hold-timer-display">
                {done ? '0:00' : fmt(secondsLeft)}
              </p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                {done ? 'Hold complete' : running ? 'Holding…' : 'Tap to start'}
              </p>
            </div>
            {!done && (
              <button
                onClick={reset}
                className="w-10 h-10 rounded-full border border-zinc-700 text-zinc-400 flex items-center justify-center hover:text-white"
                data-testid="hold-timer-reset"
                aria-label="Reset"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="mt-3 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-cyan-400 transition-all duration-300" style={{ width: `${done ? 100 : pct}%` }} />
          </div>
          {done && (
            <div className="mt-3 flex items-center justify-center gap-2 text-green-400" data-testid="hold-done">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-semibold">Nice hold — tap Next when you&apos;re ready.</span>
            </div>
          )}
        </div>
      )}

      {/* Mirror camera — see yourself (skeleton), no counting */}
      <div className={immersive ? 'flex-1 min-h-0 flex flex-col' : ''}>
        <PoseCameraErrorBoundary>
          <PoseCameraAdvanced
            mode="mirror"
            immersive={immersive}
            exerciseName={exerciseName}
            exerciseDefinition={exerciseDefinition}
            setNumber={null}
            showLiveFeedback={false}
            onCameraStateChange={onCameraStateChange}
          />
        </PoseCameraErrorBoundary>
      </div>
    </div>
  );
};

export default GuidedExercisePanel;
